import React, { useEffect, useState, useRef, useCallback } from "react";
import { Card, Spinner } from "react-bootstrap";
import ChatCard from "./ChatCard";
import { notifyError } from "../Common/Toaster";
import { getConversationList } from "../../../services/UserServices/UserService";
import { conversationFilterPost } from "../../../store/actions/FilterActions";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

const DEBOUNCE_DELAY = 500;

function ConversationList({
  type,
  userId,
  gender,
  selectedConversationIds,
  setSelectedConversationIds,
  refreshConversationList,
  refreshListTrigger,
  setIsSelectAll,
  isSelectAll,
}) {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  const [chatList, setChatList] = useState([]);
  const filterConversation = useSelector(
    (state) => state.filter.filter.conversationFilter
  );
  const [filter, setFilter] = useState(
    filterConversation ? filterConversation : "all"
  );

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [showCheckboxes, setShowCheckboxes] = useState(false);

  const loadMoreRef = useRef(null);

  const handleSelectAll = () => {
    const newValue = !isSelectAll;
    setIsSelectAll(newValue);

    if (newValue) {
      setSelectedConversationIds(chatList.map((item) => item._id));
    } else {
      setSelectedConversationIds([]);
    }
  };

  const handleCheckboxChange = (conversationId) => {
    setSelectedConversationIds((prev) => {
      let updated;

      if (prev.includes(conversationId)) {
        updated = prev.filter((id) => id !== conversationId);
      } else {
        updated = [...prev, conversationId];
      }

      if (updated.length === chatList.length && chatList.length > 0) {
        setIsSelectAll(true);
      } else {
        setIsSelectAll(false);
      }

      return updated;
    });
  };

  const fetchChatList = async (pageNumber, replace = false) => {
    if (pageNumber === 0) setIsLoading(true);
    else setIsMoreLoading(true);

    try {
      const response = await getConversationList({
        userId,
        search: searchText,
        filter,
        page: pageNumber,
        limit: 50,
      });

      const newData = response.data.data.finalConversations || [];
      const paginationEnd = response.data.data.isPaginationEnds;

      setChatList((prev) => (replace ? newData : [...prev, ...newData]));
      if (paginationEnd) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      notifyError("Something went wrong");
      setHasMore(false);
    } finally {
      setIsLoading(false);
      setIsMoreLoading(false);
    }
  };

  // ✅ Fetch first page when search/filter changes
  useEffect(() => {
    const delay = setTimeout(() => {
      if (searchText.length >= 3 || searchText.length === 0) {
        setPage(0);
        fetchChatList(0, true);
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(delay);
  }, [searchText, refreshConversationList, refreshListTrigger, filter]);

  // ✅ Infinite scroll observer (FIXED)
  const bottomObserver = useCallback(
    (node) => {
      if (isMoreLoading || isLoading || !hasMore || !node) return;

      const scrollContainer = document.querySelector(".chat-list-scrollable");

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setPage((prev) => prev + 1);
          }
        },
        {
          root: scrollContainer,
          rootMargin: "0px",
          threshold: 0.1,
        }
      );
      observer.observe(node);
      return () => observer.disconnect();
    },
    [isMoreLoading, isLoading, hasMore]
  );

  // ✅ Trigger load more when page increments
  useEffect(() => {
    if (page > 0) fetchChatList(page);
  }, [page]);

  // ✅ Attach observer and cleanup
  useEffect(() => {
    const disconnect = bottomObserver(loadMoreRef.current);
    return () => {
      if (disconnect) {
        disconnect();
      }
    };
  }, [bottomObserver, loadMoreRef.current]);

  return (
    <Card className="chat-list shadow-md">
      <div className="d-flex justify-content-between align-items-center m-2">
        {showCheckboxes && chatList.length > 0 && (
          <div className="d-flex justify-content-between align-items-center m-2">
            <span style={{ marginTop: "4px" }}>Select All</span>
            <input
              type="checkbox"
              className="form-check-input me-2 conversation-checkbox"
              checked={isSelectAll}
              onChange={handleSelectAll}
            />
          </div>
        )}

        {!showCheckboxes && chatList.length > 0 && (
          <select
            className="form-control w-25"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              dispatch(conversationFilterPost(e.target.value));
            }}
          >
            <option value="all">All</option>
            <option value="read">Read</option>
            <option value="unread">Unread</option>
            <option value="notReplied">Not Replied</option>
            <option value="internalUsers">Internal Users</option>
          </select>
        )}

        {chatList.length > 0 && (
          <button
            className="btn btn-info btn-sm w-25 p-3"
            onClick={() => {
              if (showCheckboxes) {
                setSelectedConversationIds([]);
                setShowCheckboxes(false);
                setIsSelectAll(false);
              } else {
                setShowCheckboxes(true);
              }
            }}
          >
            {showCheckboxes ? "Cancel" : "Select"}
          </button>
        )}
      </div>

      <input
        type="search"
        placeholder="Search"
        onChange={(e) => setSearchText(e.target.value)}
        className="search-input"
      />

      <div
        className="chat-list-scrollable"
        style={{ maxHeight: "70vh", overflowY: "auto", overflowX: "hidden" }}
      >
        {chatList.map((item) => (
          <div
            key={item?._id}
            className="d-flex align-items-center chat-card-item w-100"
          >

            {showCheckboxes && (
              <input
                type="checkbox"
                className="form-check-input me-2 conversation-checkbox"
                checked={selectedConversationIds.includes(item._id)}
                onChange={() => handleCheckboxChange(item._id)}
              />
            )}
            <ChatCard
              className="flex-grow-1"
              updatedAt={item?.messageTime}
              lastMessage={item?.lastMessage}
              type={item?.type}
              conversationData={item}
              unreadCount={item?.chatCount}
              userName={
                item?.sender?._id === userId
                  ? item?.receiver?.name
                  : item?.sender?.name
              }
              id={
                item?.sender?._id === userId
                  ? item?.receiver?._id
                  : item?.sender?._id
              }
              image={
                item?.sender?._id === userId
                  ? item?.receiver?.images?.[0]
                  : item?.sender?.images?.[0]
              }
              isPremium={
                item?.sender?._id === userId
                  ? item?.receiver?.isPremium
                  : item?.sender?.isPremium
              }
              gender={gender}
            />
          </div>
        ))}

        {isLoading && (
          <div className="text-center mt-3">
            <Spinner animation="border" variant="info" />
          </div>
        )}

        {!isLoading && chatList.length === 0 && (
          <h5 className="text-center">No Data</h5>
        )}

        {/* ✅ Infinite Scroll Trigger */}
        <div ref={loadMoreRef} style={{ height: "20px" }} />

        {isMoreLoading && (
          <div className="text-center mt-2">
            <Spinner animation="border" variant="info" />
          </div>
        )}
      </div>
    </Card>
  );
}

export default ConversationList;
